import React, { useState } from 'react';
import { 
  Sparkles, 
  CheckCircle2, 
  AlertCircle, 
  Copy, 
  Check, 
  Printer, 
  FileText, 
  ArrowRight, 
  Briefcase, 
  Layers, 
  PlusCircle, 
  Trash2,
  RefreshCw,
  ExternalLink,
  ChevronDown
} from 'lucide-react';
import { MasterProfile, TailorResult, JobApplication } from '../types';
import { tailorResumeWithAI } from '../services/aiService';
import { storageService } from '../services/storageService';

interface CvTailorProps {
  masterProfile: MasterProfile;
  setMasterProfile: (p: MasterProfile) => void;
  onSaveToPipeline: (app: JobApplication) => void;
  onOpenOutreachWithData: (data: { company: string; role: string; emailSubject: string; emailBody: string; inMail: string }) => void;
  onOpenPrintPreview: (tailoredSummary?: string, tailoredBullets?: string[]) => void;
}

export const CvTailor: React.FC<CvTailorProps> = ({
  masterProfile,
  setMasterProfile,
  onSaveToPipeline,
  onOpenOutreachWithData,
  onOpenPrintPreview
}) => {
  // Inputs
  const [jobTitle, setJobTitle] = useState('Senior Full-Stack Developer');
  const [company, setCompany] = useState('FinTech Scaleup UK');
  const [jobDescription, setJobDescription] = useState(`We are looking for a Senior Full-Stack Engineer with strong TypeScript, React, and Node.js skills to join our high-growth platform. 
Responsibilities:
- Build and optimize scalable web services and responsive frontends.
- Implement automated testing and CI/CD pipelines.
- Collaborate on cloud infrastructure (Cloudflare, AWS) and database performance (SQL / PostgreSQL).
- Experience in REST APIs, microservices, and performance optimization is essential.`);
  const [salaryRange, setSalaryRange] = useState('£65,000 - £75,000 / £400/day');
  const [jobType, setJobType] = useState<'Permanent' | 'Contract' | 'Freelance'>('Contract');
  const [jobUrl, setJobUrl] = useState('https://jobserve.com/job/123456');

  // Processing state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [tailorResult, setTailorResult] = useState<TailorResult | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [savedToPipelineSuccess, setSavedToPipelineSuccess] = useState(false);

  // Sample quick loaders for rapid testing
  const loadSampleJD = (type: 'contract' | 'python' | 'fullstack') => {
    if (type === 'contract') {
      setJobTitle('Contract React / TypeScript Engineer');
      setCompany('Digital Agency London');
      setSalaryRange('£350 - £450 / day');
      setJobType('Contract');
      setJobDescription(`Urgent contract requirement for a Senior React / TypeScript Developer for a 3-month rolling contract (Outside IR35).
Key Skills: React, TypeScript, Next.js, Tailwind CSS, REST APIs, State Management, Git, Agile.
Must be available for immediate start to support agency sprint delivery.`);
    } else if (type === 'python') {
      setJobTitle('Python & Automation Engineer');
      setCompany('AutomateHQ UK');
      setSalaryRange('£60,000 - £70,000');
      setJobType('Permanent');
      setJobDescription(`Seeking a Python Developer with experience in automation workflows, API integration, and database systems.
Key Skills: Python, FastAPI, Django, PostgreSQL, Docker, n8n/Zapier webhooks, Cloud architecture.
Responsible for automating backend pipelines and third-party integrations.`);
    } else {
      setJobTitle('Senior Full-Stack Developer');
      setCompany('FinTech Scaleup UK');
      setSalaryRange('£65,000 - £75,000 / £400/day');
      setJobType('Contract');
      setJobDescription(`We are looking for a Senior Full-Stack Engineer with strong TypeScript, React, and Node.js skills to join our high-growth platform.
Responsibilities:
- Build and optimize scalable web services and responsive frontends.
- Implement automated testing and CI/CD pipelines.
- Collaborate on cloud infrastructure (Cloudflare, AWS) and database performance (SQL / PostgreSQL).
- Experience in REST APIs, microservices, and performance optimization is essential.`);
    }
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleRunTailoring = async () => {
    if (!jobDescription) return;
    setIsAnalyzing(true);
    setSavedToPipelineSuccess(false);

    // Format Master CV text representation for the AI
    const masterCvText = `
Name: ${masterProfile.fullName}
Title: ${masterProfile.title}
Location: ${masterProfile.location}
Summary: ${masterProfile.summary}
Skills: ${masterProfile.skills.join(', ')}
Work Experience:
${masterProfile.experiences.map(e => `* ${e.role} at ${e.company} (${e.period}):\n  ${e.bullets.join('\n  ')}`).join('\n\n')}
`;

    const result = await tailorResumeWithAI({
      jobTitle,
      company,
      jobDescription,
      masterCvText
    });

    setTailorResult(result);
    setIsAnalyzing(false);
  };

  const handleSaveToPipeline = () => {
    const newApp: JobApplication = {
      id: `app_${Date.now()}`,
      company: company.trim() || 'Target Company',
      role: jobTitle.trim() || 'Software Engineer',
      location: 'UK Remote / Hybrid',
      salary_range: salaryRange,
      job_type: jobType,
      status: 'target',
      job_url: jobUrl,
      contact_person: '',
      contact_email: '',
      match_score: tailorResult?.matchScore || 85,
      tailored_cv_summary: tailorResult?.tailoredSummary || '',
      notes: `Matched via Cloudflare AI. Top keywords: ${tailorResult?.matchingKeywords.slice(0, 3).join(', ')}`,
      applied_date: new Date().toISOString().split('T')[0]
    };

    onSaveToPipeline(newApp);
    setSavedToPipelineSuccess(true);
  };

  return (
    <div className="space-y-8 pb-16">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800/80 pb-6">
        <div>
          <div className="inline-flex items-center space-x-2 px-2.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Cloudflare Workers AI (Llama 3.1)</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">AI ATS CV Tailor & Keyword Matcher</h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Paste any UK job description. Instantly extract required ATS keywords, rewrite experience bullets, and maximize interview conversion.
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowProfileEditor(!showProfileEditor)}
            className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 transition-all"
          >
            <FileText className="w-4 h-4 text-sky-400" />
            <span>{showProfileEditor ? 'Hide Master CV' : 'Edit Master CV'}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showProfileEditor ? 'rotate-180' : ''}`} />
          </button>

          <button
            onClick={() => onOpenPrintPreview(tailorResult?.tailoredSummary, tailorResult?.tailoredBullets)}
            className="flex items-center space-x-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white shadow-md shadow-indigo-600/20 transition-all"
          >
            <Printer className="w-4 h-4" />
            <span>Print ATS PDF</span>
          </button>
        </div>
      </div>

      {/* Master CV Inline Editor (Collapsible) */}
      {showProfileEditor && (
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4 animate-in fade-in duration-200">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div>
              <h3 className="text-sm font-bold text-white">Your Master CV Profile</h3>
              <p className="text-xs text-slate-400">All tailoring is generated against this base profile</p>
            </div>
            <button
              onClick={() => {
                storageService.saveProfile(masterProfile);
                setShowProfileEditor(false);
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              Save Profile Changes
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="text-slate-400 font-medium">Full Name</label>
              <input
                type="text"
                value={masterProfile.fullName}
                onChange={(e) => setMasterProfile({ ...masterProfile, fullName: e.target.value })}
                className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-white"
              />
            </div>
            <div>
              <label className="text-slate-400 font-medium">Professional Title</label>
              <input
                type="text"
                value={masterProfile.title}
                onChange={(e) => setMasterProfile({ ...masterProfile, title: e.target.value })}
                className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-white"
              />
            </div>
            <div>
              <label className="text-slate-400 font-medium">Email / Phone / Location</label>
              <input
                type="text"
                value={`${masterProfile.email} | ${masterProfile.location}`}
                onChange={(e) => {
                  const parts = e.target.value.split('|');
                  setMasterProfile({ ...masterProfile, email: parts[0]?.trim() || '', location: parts[1]?.trim() || '' });
                }}
                className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-white"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 font-medium">Master Skills (Comma separated)</label>
            <input
              type="text"
              value={masterProfile.skills.join(', ')}
              onChange={(e) => setMasterProfile({ ...masterProfile, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-xs text-sky-300 font-mono"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400 font-medium">Professional Summary</label>
            <textarea
              rows={3}
              value={masterProfile.summary}
              onChange={(e) => setMasterProfile({ ...masterProfile, summary: e.target.value })}
              className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-xs text-slate-200"
            />
          </div>
        </div>
      )}

      {/* Main Two-Column Layout: Job Input on Left, AI Output on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Target Job Input */}
        <div className="lg:col-span-5 space-y-4">
          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4 shadow-lg">
            
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center space-x-2">
                <Briefcase className="w-4 h-4 text-sky-400" />
                <span>Target Job Details</span>
              </h3>
              
              {/* Quick Load Sample Buttons */}
              <div className="flex items-center space-x-1">
                <span className="text-[10px] text-slate-500 mr-1">Load sample:</span>
                <button
                  type="button"
                  onClick={() => loadSampleJD('contract')}
                  className="px-2 py-0.5 rounded text-[10px] bg-slate-800 hover:bg-sky-500/20 text-sky-300 border border-slate-700"
                >
                  Contract
                </button>
                <button
                  type="button"
                  onClick={() => loadSampleJD('python')}
                  className="px-2 py-0.5 rounded text-[10px] bg-slate-800 hover:bg-amber-500/20 text-amber-300 border border-slate-700"
                >
                  Python
                </button>
                <button
                  type="button"
                  onClick={() => loadSampleJD('fullstack')}
                  className="px-2 py-0.5 rounded text-[10px] bg-slate-800 hover:bg-emerald-500/20 text-emerald-300 border border-slate-700"
                >
                  FullStack
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-400">Target Role</label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Senior React Engineer"
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-sky-500"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-400">Company Name</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. Nexus Tech UK"
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-sky-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-400">Salary / Day Rate</label>
                <input
                  type="text"
                  value={salaryRange}
                  onChange={(e) => setSalaryRange(e.target.value)}
                  placeholder="e.g. £350/day or £70,000"
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-sky-500"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-400">Job Type</label>
                <select
                  value={jobType}
                  onChange={(e) => setJobType(e.target.value as any)}
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-sky-500"
                >
                  <option value="Contract">Contract (Outside IR35)</option>
                  <option value="Permanent">Permanent</option>
                  <option value="Freelance">Freelance</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-400">Paste Job Description (or requirements)</label>
              <textarea
                rows={8}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the full job posting text here..."
                className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500 leading-relaxed font-mono"
              />
            </div>

            <button
              onClick={handleRunTailoring}
              disabled={isAnalyzing || !jobDescription}
              className="w-full py-3 px-4 rounded-xl font-bold text-sm bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white shadow-lg shadow-sky-500/25 flex items-center justify-center space-x-2 transition-all disabled:opacity-50"
            >
              {isAnalyzing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Analyzing with Cloudflare AI...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Tailor CV & Extract ATS Keywords</span>
                </>
              )}
            </button>

          </div>
        </div>

        {/* Right Column: Tailored Results & ATS Score */}
        <div className="lg:col-span-7 space-y-6">
          
          {tailorResult ? (
            <div className="space-y-6 animate-in fade-in duration-300">
              
              {/* ATS Match Score Card */}
              <div className="rounded-2xl bg-gradient-to-r from-slate-900 to-slate-900/90 border border-slate-800 p-6 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
                <div className="flex items-center space-x-4">
                  <div className="relative flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full border-4 border-slate-800 flex items-center justify-center">
                      <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-tr from-emerald-400 to-sky-400">
                        {tailorResult.matchScore}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">ATS Alignment Score</span>
                    <h3 className="text-base font-bold text-white">High Interview Probability</h3>
                    <p className="text-xs text-slate-400 mt-1 max-w-sm">{tailorResult.scoreRationale}</p>
                  </div>
                </div>

                <div className="flex sm:flex-col gap-2 w-full sm:w-auto">
                  <button
                    onClick={handleSaveToPipeline}
                    disabled={savedToPipelineSuccess}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all ${
                      savedToPipelineSuccess
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : 'bg-sky-500 hover:bg-sky-400 text-white shadow-md shadow-sky-500/20'
                    }`}
                  >
                    {savedToPipelineSuccess ? <Check className="w-3.5 h-3.5" /> : <PlusCircle className="w-3.5 h-3.5" />}
                    <span>{savedToPipelineSuccess ? 'Saved to Pipeline!' : 'Add to Job Tracker'}</span>
                  </button>

                  <button
                    onClick={() => onOpenOutreachWithData({
                      company,
                      role: jobTitle,
                      emailSubject: tailorResult.coldEmailOutreach.subject,
                      emailBody: tailorResult.coldEmailOutreach.body,
                      inMail: tailorResult.linkedInInMail
                    })}
                    className="px-3 py-2 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center space-x-1.5"
                  >
                    <span>Create Cold Pitch</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Keywords Breakdown */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Matching Keywords */}
                <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4 space-y-2.5">
                  <div className="flex items-center space-x-2 text-xs font-bold text-emerald-400">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span>Matching ATS Keywords ({tailorResult.matchingKeywords.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {tailorResult.matchingKeywords.map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md text-[11px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 font-medium">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Missing / High Priority Keywords */}
                <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4 space-y-2.5">
                  <div className="flex items-center space-x-2 text-xs font-bold text-amber-400">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>Added Target Keywords ({tailorResult.missingKeywords.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {tailorResult.missingKeywords.map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md text-[11px] bg-amber-500/10 text-amber-300 border border-amber-500/20 font-medium">
                        + {kw}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tailored Summary */}
              <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-sky-400 uppercase tracking-wider">Tailored Executive Summary</span>
                  <button
                    onClick={() => handleCopy(tailorResult.tailoredSummary, 'summary')}
                    className="flex items-center space-x-1 text-xs text-slate-400 hover:text-white"
                  >
                    {copiedKey === 'summary' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedKey === 'summary' ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
                <p className="text-xs sm:text-sm text-slate-200 leading-relaxed bg-slate-950/60 p-3.5 rounded-xl border border-slate-800/80">
                  {tailorResult.tailoredSummary}
                </p>
              </div>

              {/* Tailored Experience Bullets */}
              <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-sky-400 uppercase tracking-wider">High-Impact Tailored Bullets (ATS Formatted)</span>
                  <button
                    onClick={() => handleCopy(tailorResult.tailoredBullets.map(b => `• ${b}`).join('\n'), 'bullets')}
                    className="flex items-center space-x-1 text-xs text-slate-400 hover:text-white"
                  >
                    {copiedKey === 'bullets' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedKey === 'bullets' ? 'Copied All' : 'Copy All'}</span>
                  </button>
                </div>
                <div className="space-y-2.5">
                  {tailorResult.tailoredBullets.map((bullet, idx) => (
                    <div key={idx} className="flex items-start space-x-2.5 bg-slate-950/60 p-3 rounded-xl border border-slate-800/80 group">
                      <span className="text-sky-400 font-bold shrink-0 text-sm mt-0.5">•</span>
                      <p className="text-xs text-slate-200 leading-relaxed flex-1">{bullet}</p>
                      <button
                        onClick={() => handleCopy(bullet, `bullet_${idx}`)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-slate-400 hover:text-white"
                        title="Copy bullet"
                      >
                        {copiedKey === `bullet_${idx}` ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-slate-800 p-12 text-center space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center mx-auto text-slate-500">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="space-y-1 max-w-sm mx-auto">
                <h4 className="text-sm font-bold text-white">No Job Analysis Generated Yet</h4>
                <p className="text-xs text-slate-400">
                  Select a sample role on the left or paste your target job description, then click <strong>Tailor CV & Extract ATS Keywords</strong>.
                </p>
              </div>
            </div>
          )}

        </div>

      </div>

    </div>
  );
};
